import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import {
  Input,
  EmptyState,
  ErrorNotice,
  LoadingScreen,
  PageHeader,
  StatusBadge,
} from '../components/ui';
import { api } from '../lib/api';
import { money } from '../lib/format';
import type {
  ArtistSearchResult,
  PopularTag,
  PublicServiceSearchResult,
  SearchScope,
} from '../types';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const SCOPES: { value: SearchScope; label: string; placeholder: string; hint: string }[] = [
  {
    value: 'services',
    label: 'Услуга',
    placeholder: 'Название или тег услуги',
    hint: 'Что можно заказать прямо сейчас',
  },
  {
    value: 'orders',
    label: 'Заказ',
    placeholder: 'Тег выполненной работы',
    hint: 'Кто уже рисовал такое',
  },
  {
    value: 'artists',
    label: 'Художник',
    placeholder: 'Имя или ник',
    hint: 'Поиск по имени и нику',
  },
];

/**
 * Поиск по каталогу. Вкладки отвечают на разные вопросы: «что купить»,
 * «кто это уже рисовал» и «где найти конкретного человека», поэтому у каждой
 * своя выдача и свой источник тегов.
 */
export function ArtistSearchPage() {
  const [scope, setScope] = useState<SearchScope>('services');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const activeScope = SCOPES.find((item) => item.value === scope) ?? SCOPES[0]!;

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const textSearchEnabled = debounced.length >= MIN_QUERY_LENGTH;
  const searchEnabled = textSearchEnabled || selectedTag !== null;
  const searchParameters = () => {
    const parameters = new URLSearchParams();
    if (textSearchEnabled) parameters.set('query', debounced);
    if (selectedTag) parameters.set('tag', selectedTag);
    return parameters;
  };

  // Две выборки вместо одной с объединённым типом: у услуг и художников разные
  // ответы, и разбирать их в одном запросе пришлось бы приведением типов.
  const serviceResults = useQuery({
    queryKey: ['artist-search', 'services', textSearchEnabled ? debounced : '', selectedTag],
    queryFn: () =>
      api<{ items: PublicServiceSearchResult[] }>(`/public/services?${searchParameters()}`, {
        auth: false,
      }),
    // Вкладка «Услуга» — витрина: без запроса показываем, что можно заказать сейчас.
    enabled: scope === 'services',
  });
  const artistResults = useQuery({
    queryKey: ['artist-search', scope, textSearchEnabled ? debounced : '', selectedTag],
    queryFn: () => {
      const parameters = searchParameters();
      parameters.set('scope', scope);

      return api<{ items: ArtistSearchResult[] }>(`/public/artists?${parameters}`, { auth: false });
    },
    enabled: searchEnabled && scope !== 'services',
  });
  const popularTags = useQuery({
    queryKey: ['artist-search', 'popular-tags', scope],
    queryFn: () =>
      api<{ items: PopularTag[] }>(
        `/public/artists/tags?scope=${scope === 'services' ? 'services' : 'orders'}`,
        { auth: false },
      ),
    staleTime: 60_000,
    // У вкладки «Художник» ищут человека, теги там ни при чём.
    enabled: scope !== 'artists',
  });
  const featured = useQuery({
    queryKey: ['artist-search', 'featured'],
    queryFn: () =>
      api<{ items: ArtistSearchResult[] }>('/public/artists/featured', { auth: false }),
    staleTime: 30_000,
  });

  const chooseTag = (tag: string) => {
    setQuery('');
    setDebounced('');
    setSelectedTag((current) => (current === tag ? null : tag));
  };

  return (
    <>
      <PageHeader eyebrow="Каталог" title="Найти художника" />
      <div className="tabs tabs--scroll" role="tablist" aria-label="Что искать">
        {SCOPES.map((item) => (
          <button
            key={item.value}
            role="tab"
            aria-selected={scope === item.value}
            className={scope === item.value ? 'is-active' : ''}
            onClick={() => {
              setScope(item.value);
              setSelectedTag(null);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <Input
        autoFocus
        value={query}
        placeholder={activeScope.placeholder}
        aria-label="Поиск по каталогу"
        onChange={(event) => {
          const value = event.target.value;
          setQuery(value);
          if (value.trim()) setSelectedTag(null);
        }}
      />
      <p className="field__hint search-hint">{activeScope.hint}</p>

      {query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
        <p className="field__hint search-hint">Введите ещё один символ</p>
      )}

      {popularTags.error && <ErrorNotice error={popularTags.error} />}
      {popularTags.data && popularTags.data.items.length > 0 && (
        <section className="search-tags" aria-labelledby="popular-tags-title">
          <div className="section-heading section-heading--compact">
            <h2 id="popular-tags-title">
              {scope === 'services' ? 'Популярные услуги' : 'Популярные заказы'}
            </h2>
          </div>
          <div className="chip-row">
            {popularTags.data.items.map((item) => (
              <button
                key={item.tag}
                type="button"
                className={`chip ${selectedTag === item.tag ? 'is-active' : ''}`}
                aria-pressed={selectedTag === item.tag}
                onClick={() => chooseTag(item.tag)}
              >
                #{item.tag}
                <span className="chip__count">{item.count}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/*
        «Услуга» — витрина, а не только поиск: без запроса она показывает, что
        можно заказать прямо сейчас. Иначе вкладка выглядит пустой, пока не
        угадаешь слово, и каталог кажется сломанным.
      */}
      {scope === 'services' ? (
        <ServiceResults
          services={serviceResults.data?.items}
          pending={serviceResults.isPending}
          error={serviceResults.error}
          title={
            selectedTag
              ? `Услуги по тегу #${selectedTag}`
              : textSearchEnabled
                ? 'Найденные услуги'
                : 'Можно заказать сейчас'
          }
          empty={
            searchEnabled
              ? 'Попробуйте другой тег или название услуги.'
              : 'Художники ещё не выложили услуги. Загляните во вкладку «Художник».'
          }
        />
      ) : searchEnabled ? (
        <SearchResults
          artists={artistResults.data?.items}
          pending={artistResults.isPending}
          error={artistResults.error}
          title={selectedTag ? `Художники по тегу #${selectedTag}` : 'Результаты поиска'}
        />
      ) : (
        <FeaturedArtists
          artists={featured.data?.items}
          pending={featured.isPending}
          error={featured.error}
        />
      )}
    </>
  );
}

function SearchResults({
  artists,
  pending,
  error,
  title,
}: {
  artists?: ArtistSearchResult[];
  pending: boolean;
  error: unknown;
  title: string;
}) {
  return (
    <section aria-labelledby="search-results-title">
      <div className="section-heading">
        <h2 id="search-results-title">{title}</h2>
      </div>
      {pending && <LoadingScreen label="Ищем художников" />}
      {error ? <ErrorNotice error={error} /> : null}
      {artists?.length === 0 && (
        <EmptyState
          title="Никого не нашли"
          text="Попробуйте другой тег, имя или ник — или загляните в подборку ниже."
        />
      )}
      {artists && <ArtistList artists={artists} />}
    </section>
  );
}

/**
 * Услугу видно целиком: обложка, цена и автор. Именно это и просят на вкладке
 * «Услуга» — сразу понятно, что покупаешь по тегу.
 */
function ServiceResults({
  services,
  pending,
  error,
  title,
  empty,
}: {
  services?: PublicServiceSearchResult[];
  pending: boolean;
  error: unknown;
  title: string;
  empty: string;
}) {
  return (
    <section aria-labelledby="search-results-title">
      <div className="section-heading">
        <h2 id="search-results-title">{title}</h2>
      </div>
      {pending && <LoadingScreen label="Ищем услуги" />}
      {error ? <ErrorNotice error={error} /> : null}
      {services?.length === 0 && <EmptyState title="Пока пусто" text={empty} />}
      <div className="list">
        {services?.map((service) => (
          <Link
            key={service.id}
            href={`/a/${service.artist.slug}`}
            className="card card--interactive service-result"
          >
            {service.previewUrl && (
              <img
                className="service-result__preview"
                src={`${service.previewUrl}?size=thumb`}
                alt=""
              />
            )}
            <div className="card-row">
              <h2>{service.title}</h2>
              {service.basePrice && (
                <strong>от {money(service.basePrice, service.currency)}</strong>
              )}
            </div>
            <p className="meta">{service.artist.displayName}</p>
            {service.tags.length > 0 && (
              <div className="tag-list" aria-label="Теги услуги">
                {service.tags.slice(0, 5).map((tag) => (
                  <span key={tag} className="tag-pill">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeaturedArtists({
  artists,
  pending,
  error,
}: {
  artists?: ArtistSearchResult[];
  pending: boolean;
  error: unknown;
}) {
  return (
    <section aria-labelledby="featured-artists-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Сейчас в ArtFlow</p>
          <h2 id="featured-artists-title">Актуальные художники</h2>
        </div>
      </div>
      {pending && <LoadingScreen label="Подбираем художников" />}
      {error ? <ErrorNotice error={error} /> : null}
      {artists?.length === 0 && (
        <EmptyState
          title="Пока никого"
          text="Скоро здесь появятся художники, готовые взять заказ."
        />
      )}
      {artists && <ArtistList artists={artists} featured />}
    </section>
  );
}

function ArtistList({
  artists,
  featured = false,
}: {
  artists: ArtistSearchResult[];
  featured?: boolean;
}) {
  return (
    <div className={featured ? 'featured-artists' : 'list'}>
      {artists.map((artist) => (
        <Link
          key={artist.slug}
          href={`/a/${artist.slug}`}
          className={`card card--interactive artist-card ${featured ? 'artist-card--featured' : ''}`}
        >
          <div className="search-result">
            <div className="profile-avatar profile-avatar--small">
              {artist.avatarUrl ? (
                <img src={`${artist.avatarUrl}?size=thumb`} alt="" />
              ) : (
                artist.displayName[0]
              )}
            </div>
            <div className="search-result__identity">
              <h2>{artist.displayName}</h2>
              <p className="meta">@{artist.slug}</p>
            </div>
            <StatusBadge tone={artist.acceptsApplications ? 'success' : 'neutral'}>
              {artist.commissionsStatus === 'OPEN'
                ? 'Открыт'
                : artist.commissionsStatus === 'WAITLIST'
                  ? 'Лист ожидания'
                  : 'Закрыт'}
            </StatusBadge>
          </div>
          {artist.description && <p className="artist-card__description">{artist.description}</p>}
          {artist.tags.length > 0 && (
            <div className="tag-list" aria-label="Теги заказов">
              {artist.tags.slice(0, 5).map((tag) => (
                <span key={tag} className="tag-pill">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </Link>
      ))}
    </div>
  );
}
